import { Rocket } from "lucide-react";
import LabTemplate from "@/components/prolab/LabTemplate";

/**
 * AI Entrepreneurship Lab - Flagship ProLab Sample
 * Demonstrates full template with missions, AI tools, assessments, and outputs
 */
export default function AIEntrepreneurshipLab() {
  // Lab configuration
  const labConfig = {
    labId: "ai-entrepreneurship",
    labName: "AI Entrepreneurship Lab",
    labSubtitle: "Build and launch AI-powered business ventures",
    labDescription: "Master the fundamentals of AI-powered entrepreneurship, from ideation to launch.",
    labColor: "#1E3A8A",
    labIcon: Rocket,
    category: "Enterprise & Business Labs",
    
    // Lab Overview
    overview: {
      purpose: "The AI Entrepreneurship Lab teaches you how to identify business opportunities, leverage AI tools for rapid prototyping, and build scalable ventures. You'll learn to use AI as a force multiplier in every stage of the entrepreneurial journey—from market research to product development to customer acquisition.",
      applications: [
        "Identify and validate AI-powered business opportunities",
        "Use AI tools for rapid market research and competitive analysis",
        "Build AI-enhanced products and services",
        "Develop AI-driven marketing and growth strategies",
        "Create investor-ready business plans and pitches"
      ],
      skills: [
        "Business Ideation",
        "Market Analysis",
        "AI Integration",
        "Product Development",
        "Growth Strategy",
        "Pitch Development",
        "Financial Modeling",
        "Customer Discovery"
      ]
    },

    // Missions
    missions: [
      {
        id: "mission-1",
        title: "AI Business Opportunity Identification",
        objective: "Learn to identify viable business opportunities that can be enhanced or enabled by AI technology.",
        steps: [
          "Research current AI capabilities and limitations in your industry",
          "Identify pain points in existing workflows that AI could address",
          "Use the Business Idea Generator to brainstorm 10 potential opportunities",
          "Evaluate each opportunity using the NICE framework (New, Impactful, Credible, Executable)",
          "Select your top 3 opportunities for further validation"
        ],
        aiPrompts: [
          "Generate 10 AI-powered business ideas for the [INDUSTRY] sector focusing on [PAIN POINT]",
          "Analyze the competitive landscape for AI solutions in [MARKET]. Who are the key players and what gaps exist?",
          "Evaluate this business idea using the NICE framework: [YOUR IDEA]"
        ],
        expectedOutput: "A documented list of 3 validated AI business opportunities with preliminary market analysis.",
        checklist: [
          "Completed industry AI capability research",
          "Identified at least 5 pain points",
          "Generated 10+ business ideas",
          "Applied NICE framework to all ideas",
          "Selected top 3 opportunities"
        ]
      },
      {
        id: "mission-2",
        title: "Customer Discovery & Validation",
        objective: "Validate your business idea through systematic customer discovery and market research.",
        steps: [
          "Define your target customer persona using AI assistance",
          "Create a customer interview guide with key validation questions",
          "Conduct at least 5 customer discovery interviews",
          "Use AI to analyze interview transcripts for patterns",
          "Document key insights and pivot decisions"
        ],
        aiPrompts: [
          "Create a detailed customer persona for [YOUR TARGET MARKET]. Include demographics, psychographics, pain points, and buying behavior.",
          "Generate 15 customer discovery interview questions to validate [YOUR BUSINESS IDEA]",
          "Analyze these customer interview notes and identify the top 5 recurring themes: [INTERVIEW NOTES]"
        ],
        expectedOutput: "Customer persona document and validated problem-solution fit report.",
        checklist: [
          "Created detailed customer persona",
          "Developed interview guide",
          "Conducted 5+ customer interviews",
          "Analyzed interview data with AI",
          "Documented key findings"
        ]
      },
      {
        id: "mission-3",
        title: "AI-Powered Market Analysis",
        objective: "Conduct comprehensive market analysis using AI tools to understand market size, trends, and competition.",
        steps: [
          "Define your total addressable market (TAM), serviceable addressable market (SAM), and serviceable obtainable market (SOM)",
          "Use AI to analyze industry reports and market trends",
          "Map the competitive landscape and identify your positioning",
          "Identify market entry barriers and opportunities",
          "Create a market analysis summary report"
        ],
        aiPrompts: [
          "Calculate TAM, SAM, and SOM for [YOUR BUSINESS IDEA] in [GEOGRAPHIC REGION]",
          "Analyze the competitive landscape for [MARKET]. Create a positioning map and identify gaps.",
          "What are the key market trends affecting [INDUSTRY] over the next 5 years?"
        ],
        expectedOutput: "Comprehensive market analysis report with TAM/SAM/SOM calculations.",
        checklist: [
          "Calculated TAM, SAM, SOM",
          "Analyzed 3+ industry reports",
          "Created competitive positioning map",
          "Identified market barriers",
          "Completed market analysis report"
        ]
      },
      {
        id: "mission-4",
        title: "Business Model Design",
        objective: "Design a scalable business model using the Business Model Canvas framework.",
        steps: [
          "Complete all 9 sections of the Business Model Canvas",
          "Use AI to stress-test your value proposition",
          "Identify and validate your key revenue streams",
          "Map your cost structure and unit economics",
          "Create a business model summary presentation"
        ],
        aiPrompts: [
          "Help me complete a Business Model Canvas for [YOUR BUSINESS]. Start with the value proposition.",
          "Analyze the unit economics for [YOUR BUSINESS MODEL]. What metrics should I track?",
          "What are potential revenue streams for [YOUR BUSINESS] beyond [CURRENT MODEL]?"
        ],
        expectedOutput: "Completed Business Model Canvas with unit economics analysis.",
        checklist: [
          "Completed all 9 canvas sections",
          "Validated value proposition",
          "Defined revenue streams",
          "Calculated unit economics",
          "Created summary presentation"
        ]
      },
      {
        id: "mission-5",
        title: "MVP Definition & Prototyping",
        objective: "Define your Minimum Viable Product and create a rapid prototype using AI tools.",
        steps: [
          "List all potential features and prioritize using MoSCoW method",
          "Define your MVP scope (must-have features only)",
          "Use AI to create user stories and acceptance criteria",
          "Build a clickable prototype or proof of concept",
          "Plan your MVP development timeline"
        ],
        aiPrompts: [
          "Help me prioritize these features for my MVP using the MoSCoW method: [FEATURE LIST]",
          "Write user stories with acceptance criteria for [MVP FEATURE]",
          "Create a 2-week sprint plan to build an MVP for [YOUR PRODUCT]"
        ],
        expectedOutput: "MVP specification document and clickable prototype.",
        checklist: [
          "Prioritized all features",
          "Defined MVP scope",
          "Created user stories",
          "Built prototype/POC",
          "Created development timeline"
        ]
      },
      {
        id: "mission-6",
        title: "Go-to-Market Strategy",
        objective: "Develop a comprehensive go-to-market strategy for your AI-powered venture.",
        steps: [
          "Define your positioning and messaging framework",
          "Identify your primary and secondary marketing channels",
          "Create an AI-powered content strategy",
          "Design your customer acquisition funnel",
          "Set launch goals and metrics"
        ],
        aiPrompts: [
          "Create a positioning statement for [YOUR PRODUCT] targeting [YOUR AUDIENCE]",
          "Develop a content marketing strategy for launching [YOUR PRODUCT] with a budget of [BUDGET]",
          "What are the best customer acquisition channels for [YOUR BUSINESS MODEL]?"
        ],
        expectedOutput: "Go-to-market strategy document with channel plan and launch timeline.",
        checklist: [
          "Created positioning statement",
          "Selected marketing channels",
          "Developed content strategy",
          "Designed acquisition funnel",
          "Set measurable launch goals"
        ]
      },
      {
        id: "mission-7",
        title: "Financial Projections & Planning",
        objective: "Create financial projections and understand the economics of your venture.",
        steps: [
          "Build a 3-year revenue projection model",
          "Create expense forecasts by category",
          "Calculate key financial metrics (LTV, CAC, runway)",
          "Model different growth scenarios",
          "Prepare financial summary for investors"
        ],
        aiPrompts: [
          "Help me create a 3-year revenue projection for [YOUR BUSINESS] with these assumptions: [ASSUMPTIONS]",
          "Calculate LTV and CAC for a SaaS business with [PRICING] and [CHURN RATE]",
          "What financial metrics do investors want to see for a [STAGE] [INDUSTRY] startup?"
        ],
        expectedOutput: "Financial model with 3-year projections and key metrics.",
        checklist: [
          "Built revenue projection model",
          "Created expense forecast",
          "Calculated LTV, CAC, runway",
          "Modeled growth scenarios",
          "Prepared investor summary"
        ]
      },
      {
        id: "mission-8",
        title: "Pitch Deck & Investor Readiness",
        objective: "Create a compelling pitch deck and prepare for investor conversations.",
        steps: [
          "Structure your pitch using proven frameworks",
          "Create compelling slides for each section",
          "Use AI to refine your narrative and messaging",
          "Practice and refine your verbal pitch",
          "Prepare for common investor questions"
        ],
        aiPrompts: [
          "Create an outline for a 10-slide pitch deck for [YOUR STARTUP]",
          "Write compelling copy for the problem and solution slides of my pitch deck: [CONTEXT]",
          "What are the top 20 questions VCs ask during a seed round pitch?"
        ],
        expectedOutput: "Complete pitch deck and Q&A preparation document.",
        checklist: [
          "Created pitch structure",
          "Designed all slides",
          "Refined narrative",
          "Practiced pitch (5+ times)",
          "Prepared Q&A responses"
        ]
      }
    ],

    // AI Tools available in this lab
    aiTools: [
      "idea-generator",
      "project-charter",
      "business-plan",
      "communication-coach"
    ],

    // Guided Practice Exercises
    guidedPractice: [
      {
        title: "Rapid Ideation Sprint",
        scenario: "You have 30 minutes to generate and evaluate 20 AI business ideas for the healthcare industry. Use the AI Business Idea Generator and NICE framework to identify your top 3."
      },
      {
        title: "Customer Interview Simulation",
        scenario: "Practice conducting a customer discovery interview. The AI will play the role of a potential customer in the B2B software space."
      },
      {
        title: "Pitch Practice Session",
        scenario: "Deliver your 3-minute elevator pitch. The AI will provide feedback on clarity, persuasion, and areas for improvement."
      }
    ],

    // Real World Applications
    realWorldApplications: [
      {
        title: "Launch Your Side Project",
        description: "Apply your skills to validate and launch an actual side business within 30 days."
      },
      {
        title: "Startup Competition Entry",
        description: "Use your pitch deck and business plan to enter a real startup competition."
      },
      {
        title: "Client Consulting Project",
        description: "Apply your market analysis skills to help an existing business explore AI opportunities."
      }
    ],

    // Assessments
    assessments: [
      {
        title: "Business Model Knowledge Check",
        type: "Quiz",
        description: "Test your understanding of business model frameworks and canvas methodology.",
        questions: 15
      },
      {
        title: "Market Analysis Scenario",
        type: "Scenario",
        description: "Analyze a real market opportunity and make strategic recommendations.",
        questions: 5
      },
      {
        title: "Pitch Readiness Assessment",
        type: "Practical",
        description: "Deliver your pitch and receive AI-powered feedback on key metrics.",
        questions: 10
      }
    ],

    // Portfolio Outputs
    portfolioOutputs: [
      { title: "Business Opportunity Report", type: "Analysis Document" },
      { title: "Customer Persona", type: "Research Document" },
      { title: "Market Analysis Report", type: "Research Document" },
      { title: "Business Model Canvas", type: "Strategic Framework" },
      { title: "MVP Specification", type: "Product Document" },
      { title: "Go-to-Market Strategy", type: "Strategy Document" },
      { title: "Financial Projections", type: "Financial Model" },
      { title: "Investor Pitch Deck", type: "Presentation" }
    ],

    // Progress data (would come from auth system in production)
    completedMissions: ["mission-1"],
    userOutputs: [
      { title: "Business Opportunity Report", type: "Analysis Document" }
    ],
    readinessScore: 15,
    recommendedLabs: [
      { name: "Project Management AI Lab", reason: "Manage your venture development", href: "/juswork/prolab", color: "#2563EB" },
      { name: "Sales & Marketing AI Lab", reason: "Scale your customer acquisition", href: "/juswork/prolab", color: "#3B82F6" }
    ]
  };

  return <LabTemplate {...labConfig} />;
}
